import { useContext, useEffect, useState, useRef } from "react";
import CheckIcon from "@mui/icons-material/Check";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { haveValue, toast } from "../helper/common";
import TenantContext from "../context/tenant/TenantContext";
import AuthContext from "../context/auth/AuthContext";
import ApplicationContext from "../context/application/ApplicationContext";
import CloseIcon from "../assets/img/closeIcon.svg";
import UserIcon from "../assets/img/user-icon.svg";
import SkorDefaultLoader from "../assets/img/skorDefaultLoader.gif";
import useTranslator from "../hooks/useTranslator";
import { Link } from "react-router-dom";
import ConsentStepModal from "../pages/onboarding/ConsentStepModal";
import copy from "copy-to-clipboard";
import SendIconWhite from "../assets/img/sendIconWhite.svg";
import LoadingSpinner from "./LoadingSpinner";
import moment from "moment";
import "moment/locale/es";

import {
  emailPrefix,
  getInitials,
  resolveParticipantName,
  getApplicationTitle,
} from "../helper/applicationHelpers";
import { Link2Icon } from "../helper/svgHelper";

const InitialsAvatar = ({ name }) => (
  <div
    className="share-profile-avatar"
    style={{
      width: "36px",
      height: "36px",
      minWidth: "36px",
      borderRadius: "50%",
      background: "#b2ea5d",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "#1E2528",
      fontWeight: 600,
      fontSize: "13px",
    }}
  >
    {getInitials(name)}
  </div>
);

/* ── what-to-share selector ── */
const ShareWhatSelector = ({
  _t,
  myApplications,
  selected,
  onSelect,
  open,
  onToggle,
  onCloseModal,
}) => {
  const isApp = selected.type === "application" && selected.app;
  const selectedApp = isApp ? selected.app : null;
  const selectedParticipants = selectedApp?.participants ?? [];
  const selectedPendingCount = selectedParticipants.filter(
    (p) => p.consent_status === "pending"
  ).length;

  return (
    <div className="sps-wrapper">
      <div className="sps-section-header">
        <span className="sps-section-num">1</span>
        <span className="sps-section-title">{_t("whatDoYouWantToShare")}</span>
      </div>

      {/* trigger row */}
      <div
        className={`sps-trigger${open ? " sps-trigger-open" : ""}`}
        onClick={onToggle}
      >
        <div className="sps-trigger-icon">
          {!isApp ? (
            <div className="mobile-profile-info-box">
              <div className="userImageWrapper">
                <img src={UserIcon} alt="user" />
              </div>
            </div>
          ) : (
            <div className="mobile-profile-info-box">
              <div className="userImageWrapper">
                <img src={UserIcon} alt="user" />
              </div>
            </div>
          )}
        </div>
        <div className="sps-trigger-text" style={{ flex: 1 }}>
          {!isApp ? (
            <>
              <p className="sps-trigger-title sps-trigger-line-reduced">
                {_t("myProfileOnly", "My profile only")}
              </p>
              <p className="sps-trigger-sub sps-trigger-line-reduced">
                {_t(
                  "shareJustYourIndividualProfile",
                  "Share just your individual profile"
                )}
              </p>
            </>
          ) : (
            <>
              <p className="sps-trigger-title">
                {getApplicationTitle(selectedApp, _t)}
              </p>
              <div className="sps-member-chips">
                {selectedParticipants.slice(0, 5).map((p, i) => (
                  <span key={i} className="sps-chip-wrap">
                    <div
                      className={`member-avatar-chip member-avatar-chip-gray position-relative ${p.consent_status === "pending" ? "member-avatar-chip-pending" : ""}`}
                      style={{ zIndex: i }}
                    >
                      {getInitials(resolveParticipantName(p))}
                      {p.consent_status === "pending" && (
                        <span className="member-chip-warn">
                          <img src="/images/warning-dark-yellow.svg" alt="" />
                        </span>
                      )}
                    </div>
                  </span>
                ))}
              </div>
              {selectedPendingCount > 0 && (
                <div className="sps-pending-badge" style={{ marginTop: "4px" }}>
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <circle cx="6" cy="6" r="5.5" stroke="#D97706" />
                    <path
                      d="M6 3.5V6.5L7.5 8"
                      stroke="#D97706"
                      strokeWidth="1"
                      strokeLinecap="round"
                    />
                  </svg>
                  {_t("pendingConsent")} &bull; {selectedPendingCount}{" "}
                  {selectedPendingCount === 1
                    ? _t("flatmate")
                    : _t("flatmates")}
                </div>
              )}
            </>
          )}
        </div>
        <svg
          className={`sps-chevron${open ? " sps-chevron-up" : ""}`}
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
        >
          <path
            d="M4 6L8 10L12 6"
            stroke="#6B7280"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>

      {/* dropdown */}
      {open && (
        <div className="sps-dropdown">
          {/* my profile option */}
          <div
            className="sps-option"
            onClick={() => onSelect({ type: "myProfile" })}
          >
            <div className="sps-trigger-icon">
              <div className="mobile-profile-info-box">
                <div className="userImageWrapper">
                  <img src={UserIcon} alt="user" />
                </div>
              </div>
            </div>
            <div className="sps-trigger-text">
              <p className="sps-trigger-title sps-trigger-line-reduced">
                {_t("myProfileOnly", "My profile only")}
              </p>
              <p className="sps-trigger-sub sps-trigger-line-reduced">
                {_t(
                  "shareJustYourIndividualProfile",
                  "Share just your individual profile"
                )}
              </p>
            </div>
          </div>

          {/* application options */}
          {(myApplications ?? []).map((app) => {
            const participants = app.participants ?? [];
            const pendingCount = participants.filter(
              (p) => p.consent_status === "pending"
            ).length;
            return (
              <div
                key={app._id}
                className="sps-option"
                onClick={() => onSelect({ type: "application", app })}
              >
                <div className="sps-trigger-icon position-relative">
                  <div className="mobile-profile-info-box">
                    <div className="userImageWrapper">
                      <img
                        src="/images/multiple_users_icon_unfilled.svg"
                        alt="User Icon"
                      />
                    </div>
                  </div>
                  {participants?.length > 0 && (
                    <div className="member-count-wrapper d-flex align-items-center justify-content-center">
                      {participants.length}
                    </div>
                  )}
                </div>
                <div className="sps-trigger-text" style={{ flex: 1 }}>
                  <p className="sps-trigger-title sps-trigger-line-reduced">
                    {getApplicationTitle(app, _t)}
                  </p>
                  <div className="sps-member-chips">
                    {participants.slice(0, 5).map((p, i) => (
                      <span key={i} className="sps-chip-wrap">
                        <div
                          className={`member-avatar-chip member-avatar-chip-gray position-relative ${p.consent_status === "pending" ? "member-avatar-chip-pending" : ""}`}
                          style={{ zIndex: i }}
                        >
                          {getInitials(resolveParticipantName(p))}
                          {p.consent_status === "pending" && (
                            <span className="member-chip-warn">
                              <img
                                src="/images/warning-dark-yellow.svg"
                                alt=""
                              />
                            </span>
                          )}
                        </div>
                      </span>
                    ))}
                  </div>
                  {pendingCount > 0 && (
                    <div className="sps-pending-badge">
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 12 12"
                        fill="none"
                      >
                        <circle cx="6" cy="6" r="5.5" stroke="#D97706" />
                        <path
                          d="M6 3.5V6.5L7.5 8"
                          stroke="#D97706"
                          strokeWidth="1"
                          strokeLinecap="round"
                        />
                      </svg>
                      {_t("pendingConsent")} &bull; {pendingCount}{" "}
                      {pendingCount === 1 ? _t("flatmate") : _t("flatmates")}
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          {/* create new application */}
          <div
            className="sps-option sps-option-create"
            onClick={() => {
              onCloseModal();
              window.dispatchEvent(new CustomEvent("openApplicationsModal"));
            }}
          >
            <div className="sps-create-icon">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M8 3V13M3 8H13"
                  stroke="#6B7280"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
            </div>
            <div className="sps-trigger-text">
              <p
                className="sps-trigger-title sps-trigger-line-reduced"
                style={{ color: "#374151" }}
              >
                {_t("createNewApplication", "Create new application")}
              </p>
              <p className="sps-trigger-sub sps-trigger-line-reduced">
                {_t("inviteFlatmatesByEmail", "Invite flatmates by email")}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ShareProfileModal = ({
  isOpen,
  onClose,
  loadLandlords,
  setAllLandlords,
  landlordsOrig,
  setAllLandlordsOrig,
  searchText,
  setSearchText,
  loading1,
  landlords,
  setShowRemove,
  shareProfileFun,
  setRemoveLandlordId,
  processingLandlordId,
  myApplications,
  shareApplicationWithLandlord,
}) => {
  const { _t, i18n } = useTranslator();
  const authContext = useContext(AuthContext);
  const { user, loadUserTenant } = authContext;
  const tenantContext = useContext(TenantContext);
  const {
    fullProfileShared,
    loadSharedProfiles,
    checkLandlordExists,
    sendRequestToLandlordToShareProfile,
    getTenantShareProfileRequestEntries,
    cancelTenantShareProfileRequest,
  } = tenantContext;
  const {
    reloadApplications,
    landlordRequests,
    landlordRequestsLoading,
    loadLandlordRequests,
    acceptLandlordRequest,
    rejectLandlordRequest,
  } = useContext(ApplicationContext);
  const queryParam = new URLSearchParams(window.location.search);
  const [filteredShortlisted, setFilteredShortlisted] = useState([]);
  const [showConsent, setShowConsent] = useState(false);
  const [selectedLandlord, setSelectedLandlord] = useState({});
  const [email, setEmail] = useState("");
  const [emailChecking, setEmailChecking] = useState(false);
  const [emailExists, setEmailExists] = useState(null);
  const [emailLandlordData, setEmailLandlordData] = useState(null);
  const [emailTouched, setEmailTouched] = useState(false);
  const [sendingEmailRequest, setSendingEmailRequest] = useState(false);
  const [pendingShareRequests, setPendingShareRequests] = useState([]);
  const [loadingPendingRequests, setLoadingPendingRequests] = useState(false);
  const [cancelingRequestId, setCancelingRequestId] = useState(null);
  const emailDebounceRef = useRef(null);

  const [shareSelector, setShareSelector] = useState({ type: "myProfile" });
  const [shareSelectorOpen, setShareSelectorOpen] = useState(false);

  const [requestShareSelectors, setRequestShareSelectors] = useState({});
  const [requestDropdownOpen, setRequestDropdownOpen] = useState({});
  const [processingRequestId, setProcessingRequestId] = useState(null);

  const isValidEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(String(email).toLowerCase());
  };

  useEffect(() => {
    if (!emailTouched) return;

    clearTimeout(emailDebounceRef.current);

    emailDebounceRef.current = setTimeout(async () => {
      if (!email || !isValidEmail(email)) {
        setEmailExists(null);
        return;
      }

      try {
        setEmailChecking(true);
        const res = await checkLandlordExists(email);
        if (res?.exists) {
          setEmailExists(true);
          setEmailLandlordData(res);
        } else {
          setEmailExists(false);
          setEmailLandlordData(null);
        }
      } catch {
        setEmailExists(false);
        setEmailLandlordData(null);
      } finally {
        setEmailChecking(false);
      }
    }, 600);

    return () => clearTimeout(emailDebounceRef.current);
  }, [email, emailTouched]);

  useEffect(() => {
    const userid = localStorage.getItem("saveduserid");
    if (user && user?._id) {
      loadSharedProfiles(user?._id, "full");
    } else if (haveValue(userid)) {
      loadSharedProfiles(userid, "full");
    }
  }, []);

  const loadPendingShareRequests = async () => {
    setLoadingPendingRequests(true);
    try {
      const res = await getTenantShareProfileRequestEntries();
      if (res?.success) {
        setPendingShareRequests(Array.isArray(res?.data) ? res.data : []);
      } else {
        setPendingShareRequests([]);
      }
    } catch (error) {
      console.log(error);
      setPendingShareRequests([]);
    } finally {
      setLoadingPendingRequests(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadPendingShareRequests();
      const applicationId = new URLSearchParams(window.location.search).get(
        "applicationId"
      );
      if (applicationId && myApplications) {
        const app = myApplications.find((a) => a._id === applicationId);
        if (app) {
          setShareSelector({ type: "application", app });
        }
      }
    }
  }, [isOpen]);

  useEffect(() => {
    setFilteredShortlisted(fullProfileShared);
    setAllLandlords((prev) => [...prev]);
    setAllLandlordsOrig((prev) => [...prev]);
  }, [fullProfileShared]);

  const filterlandlord = (searchText) => {
    const temp = [...landlordsOrig];
    const filtered = temp.filter((val) =>
      `${val?.fname_companyName} ${val?.lname}`
        .toLowerCase()
        .includes(searchText.toLowerCase())
    );
    setAllLandlords(filtered);
  };

  const isPropertyLinked = (landlordid) => {
    for (const profileShared of fullProfileShared) {
      if (
        profileShared.landlordid === landlordid &&
        haveValue(profileShared?.propertyid)
      ) {
        return profileShared;
      }
    }
    return null;
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      const effect_func = async () => {
        await loadLandlords();

        if (haveValue(queryParam.get("filter"))) {
          const name = queryParam.get("filter");
          setSearchText(name);
          filterlandlord(name);
        }
      };
      if ((searchText ?? "").length > 0) {
        effect_func();
      }
    }, 250);

    return () => clearTimeout(handler);
  }, [searchText]);

  const handlecopyUrl = () => {
    copy(
      `${window.location.protocol}//${process.env.REACT_APP_URL_NAME}/profile-request?sharedProfileLink=${user?._id}`
    );
    toast.success(_t("linkCopied"));
  };

  const getLandlordEmail = (landlordData) =>
    landlordData?.email ||
    landlordData?.landlord_email ||
    landlordData?.landlord_id?.email;

  const shouldShowConsent = (landlordData) => {
    const insuranceRequired =
      landlordData?.insurance_service_status === true &&
      (!haveValue(user?.consents?.gdpr_consent) ||
        !haveValue(user?.consents?.skor_consent));

    const affidavitRequired =
      landlordData?.affidavit_activated === true &&
      !haveValue(user?.consents?.declaration_consent);

    return insuranceRequired || affidavitRequired;
  };

  const sendShareRequest = async (landlordEmail) => {
    if (!landlordEmail) return;

    setSendingEmailRequest(true);
    try {
      const res = await sendRequestToLandlordToShareProfile(
        landlordEmail.trim(),
        shareSelector?.app?._id
      );

      if (res?.success) {
        // Also call the application share endpoint when sharing with an application
        if (shareSelector?.type === "application" && shareSelector?.app?._id) {
          const landlordId = emailLandlordData?._id || selectedLandlord?._id;
          if (landlordId) {
            await shareApplicationWithLandlord(
              landlordId,
              shareSelector.app._id
            ).catch(() => {});
          }
        }

        toast.success(res?.message || _t("requestSentSuccessfully"));
        setEmail("");
        setEmailTouched(false);
        setEmailExists(null);
        setEmailLandlordData(null);
        setSelectedLandlord({});
        await loadPendingShareRequests();
        return;
      }

      const errorMsg = res?.key
        ? `${_t(res.key)}${res.name ? ` ${res.name}` : ""}`
        : res?.message || _t("failedToShareProfile");
      toast.error(errorMsg);
    } catch (error) {
      console.log(error);
      toast.error(_t("failedToShareProfile"));
    } finally {
      setSendingEmailRequest(false);
    }
  };

  const handleLandlordRequestFlow = async (landlordData) => {
    const landlordEmail = getLandlordEmail(landlordData);
    if (!landlordEmail) return;

    if (shouldShowConsent(landlordData)) {
      setSelectedLandlord({
        ...landlordData,
        email: landlordEmail,
      });
      setShowConsent(true);
      return;
    }

    await sendShareRequest(landlordEmail);
  };

  const handleSendShareRequest = async () => {
    if (!isValidEmail(email) || emailChecking || emailExists !== true) {
      return;
    }

    await handleLandlordRequestFlow({
      email: email.trim(),
      insurance_service_status: emailLandlordData?.insurance_service_status,
      affidavit_activated: emailLandlordData?.affidavit_activated,
    });
  };

  const getPendingRequestEmail = (request) =>
    request?.landlord_id?.email ||
    request?.landlord_email ||
    request?.email ||
    request?.landlordEmail ||
    "";

  const getPendingRequestDisplayName = (request) => {
    const fname =
      request?.landlord_id?.fname || request?.landlord_id?.firstname || "";
    const lname =
      request?.landlord_id?.lname || request?.landlord_id?.lastname || "";
    const full = `${fname} ${lname}`.trim();
    if (full) return full;
    const email = getPendingRequestEmail(request);
    return emailPrefix(email) || email;
  };

  const handleCancelShareRequest = async (requestId) => {
    if (!requestId) return;

    setCancelingRequestId(requestId);
    try {
      const res = await cancelTenantShareProfileRequest(requestId);

      if (res?.success) {
        toast.success(res?.message || _t("requestCancelledSuccessfully"));
        setPendingShareRequests((prev) =>
          prev.filter((request) => request?._id !== requestId)
        );
        reloadApplications().catch(() => {});
        return;
      }

      toast.error(
        res?.key ? _t(res.key) : res?.message || _t("failedToRejectRequest")
      );
    } catch (error) {
      console.log(error);
      toast.error(_t("failedToRejectRequest"));
    } finally {
      setCancelingRequestId(null);
    }
  };

  useEffect(() => {
    if (!isOpen) {
      const url = new URL(window.location.href);
      url.searchParams.delete("shareprofilemodal");
      url.searchParams.delete("applicationId");
      window.history.pushState({}, "", url.toString());
      window.dispatchEvent(new PopStateEvent("popstate"));
    }
  }, [isOpen]);

  useEffect(() => {
    const lang = i18n.language?.toLowerCase();
    const shortLang = lang?.split("-")[0];
    moment.locale(shortLang);
  }, [i18n.language]);

  /* build the application sub-label shown under a landlord's name */
  const getApplicationBadge = (application_data) => {
    if (!application_data) return null;

    const initials = (application_data.participants ?? [])
      .slice(1, 3)
      .map((p) => getInitials(resolveParticipantName(p)))
      .join(" + ");

    return `${application_data.title}${initials ? ` · ${initials}` : ""}`;
  };

  const toggleRequestDropdown = (id) =>
    setRequestDropdownOpen((prev) => ({ ...prev, [id]: !prev[id] }));

  const selectRequestShare = (id, option) => {
    setRequestShareSelectors((prev) => ({ ...prev, [id]: option }));
    setRequestDropdownOpen((prev) => ({ ...prev, [id]: false }));
  };

  const handleAcceptLandlordRequest = async (request) => {
    const shareOption = requestShareSelectors[request._id] ?? null;
    if (!shareOption) return;
    setProcessingRequestId(request._id);
    try {
      const applicationId =
        shareOption.type === "application" ? shareOption.app?._id : undefined;
      const res = await acceptLandlordRequest(request._id, applicationId);
      if (res?.success) {
        toast.success(res?.message || _t("requestSentSuccessfully"));
        loadSharedProfiles(user?._id, "full");
      } else {
        const errorMsg = res?.key
          ? `${_t(res.key)}${res.name ? ` ${res.name}` : ""}`
          : res?.message || _t("failedToShareProfile");
        toast.error(errorMsg);
      }
    } catch {
      toast.error(_t("failedToShareProfile"));
    } finally {
      setProcessingRequestId(null);
    }
  };

  const handleRejectLandlordRequest = async (requestId) => {
    setProcessingRequestId(requestId);
    try {
      const res = await rejectLandlordRequest(requestId);
      if (!res?.success) {
        toast.error(res?.message || _t("failedToRejectRequest"));
      }
    } catch {
      toast.error(_t("failedToRejectRequest"));
    } finally {
      setProcessingRequestId(null);
    }
  };

  return (
    isOpen && (
      <div className="wrapper-modal" onClick={onClose}>
        <div
          className={`doc-history-popup total-salary-modal share-profile-modal share-profile-modal-px  ${
            isOpen ? "open" : ""
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          <span className="drawerIcon" onClick={onClose} />
          <div className="doc-history-header spm-header">
            <div>
              <h4 className="custom-modal-heading">{_t("shareYourProfile")}</h4>
              <p className="spm-subtitle">
                {_t(
                  "shareProfileSubtitle",
                  "Choose what to share and how to send it."
                )}
              </p>
            </div>
            <img
              src={CloseIcon}
              alt=""
              className="doc-history-close-btn"
              onClick={onClose}
            />
          </div>
          <div className="listWrapper">
            <div className="tabs-box-pattern">
              <div className="tabs-item d-block">
                <ShareWhatSelector
                  _t={_t}
                  myApplications={myApplications}
                  selected={shareSelector}
                  onSelect={(opt) => {
                    setShareSelector(opt);
                    setShareSelectorOpen(false);
                  }}
                  open={shareSelectorOpen}
                  onToggle={() => setShareSelectorOpen((v) => !v)}
                  onCloseModal={onClose}
                />

                <div className="sps-section-header">
                  <span className="sps-section-num">2</span>
                  <span className="sps-section-title">
                    {_t(
                      "howDoYouWantToShareIt",
                      "How do you want to share it?"
                    )}
                  </span>
                </div>

                <div className="share-profile-options-wrapper">
                  <div className="send-by-email-block">
                    <h5
                      className="mb-1 fw-bold share-profile-send-email-title"
                      style={{ color: "#253837" }}
                    >
                      {_t("sendByEmail")}
                    </h5>
                    <p>
                      {_t(
                        "sendViaEmailDescription",
                        "The owner will receive an email from Skor with a link to your profile."
                      )}
                    </p>
                    <div className="w-100">
                      <div className="d-flex align-items-center gap-2">
                        <div className="flex-grow-1 position-relative">
                          <input
                            type="email"
                            className="form-control"
                            placeholder="owner@email.com"
                            value={email}
                            onChange={(e) => {
                              setEmail(e.target.value);
                              setEmailTouched(true);
                              setEmailExists(null);
                              setEmailLandlordData(null);
                              setEmailChecking(false);
                            }}
                            style={{
                              width: "100%",
                              border:
                                !emailChecking &&
                                emailTouched &&
                                emailExists === false &&
                                isValidEmail(email)
                                  ? "1px solid #fd0000"
                                  : "1px solid #E5E7EB",
                            }}
                          />
                          {emailChecking && (
                            <div
                              style={{
                                position: "absolute",
                                right: "12px",
                                top: "50%",
                                transform: "translateY(-50%)",
                              }}
                            >
                              <LoadingSpinner
                                size={"12px"}
                                color="#253837"
                                borderWidth={1}
                                className="docs-loading-spinner"
                              />
                            </div>
                          )}
                          {!emailChecking &&
                            emailTouched &&
                            emailExists === true &&
                            isValidEmail(email) && (
                              <img
                                src="/images/checkmark-green.svg"
                                className="url-wrapper-img"
                                style={{
                                  position: "absolute",
                                  right: "12px",
                                  top: "50%",
                                  transform: "translateY(-50%)",
                                  width: "20px",
                                }}
                                alt="status"
                              />
                            )}
                        </div>
                        <button
                          type="button"
                          className="btnNext cursor-pointer d-flex align-items-center justify-content-center gap-2 email-send-btn"
                          disabled={
                            !isValidEmail(email) ||
                            emailChecking ||
                            emailExists !== true ||
                            sendingEmailRequest
                          }
                          onClick={handleSendShareRequest}
                          style={{
                            opacity:
                              !isValidEmail(email) ||
                              emailChecking ||
                              emailExists !== true ||
                              sendingEmailRequest
                                ? 0.5
                                : 1,
                          }}
                        >
                          {sendingEmailRequest ? (
                            <LoadingSpinner
                              size={"12px"}
                              color="#1e2528"
                              borderWidth={1}
                              className="docs-loading-spinner"
                            />
                          ) : (
                            <img
                              src={SendIconWhite}
                              alt="send"
                              style={{ width: "16px", filter: "brightness(0)" }}
                            />
                          )}
                          {sendingEmailRequest ? _t("sending") : _t("send")}
                        </button>
                      </div>
                      {!emailChecking &&
                        emailTouched &&
                        emailExists === false &&
                        isValidEmail(email) && (
                          <span className="error-message mt-1 mb-0 pb-0">
                            {_t("landlordDoesNotExist")}
                          </span>
                        )}
                    </div>
                  </div>

                  <p className="spm-copy-link-inline d-flex align-items-center">
                    {_t(
                      "orShareHowYouPrefer",
                      "Or share it however you prefer by copying the link"
                    )}{" "}
                    <button
                      type="button"
                      className="spm-copy-link-btn d-flex align-items-center ms-1"
                      onClick={handlecopyUrl}
                    >
                      {_t("here", "here")}
                      <Link2Icon />
                    </button>
                  </p>
                </div>

                <span className="spm-full-divider" />

                {!haveValue(searchText) && (
                  <div
                    style={{
                      textAlign: "left",
                    }}
                  >
                    <div
                      className="shareProfileHeader d-flex align-items-center gap-2 justify-content-between"
                      style={{ paddingBottom: "0px", marginBottom: "0px" }}
                    >
                      <h4
                        className="sharedWithText mb-0"
                        style={{
                          fontSize: "16px",
                          fontWeight: 600,
                          color: "#1E2528",
                        }}
                      >
                        {haveValue(searchText)
                          ? _t("searchResult")
                          : _t("peopleWithAccess")}
                      </h4>
                      <span className="spm-count-badge">
                        <img
                          src="/images/multiple_users_icon_unfilled.svg"
                          alt="users"
                          style={{
                            width: "14px",
                            height: "14px",
                            opacity: 0.5,
                          }}
                        />
                        {(pendingShareRequests?.length ?? 0) +
                          (landlordRequests?.length ?? 0) +
                          (filteredShortlisted?.length ?? 0)}{" "}
                        {_t("people")}
                      </span>
                    </div>
                  </div>
                )}

                <div className="tab-content" id="pills-tabContent">
                  <div
                    className="tab-pane fade show active"
                    id="pills-profile"
                    role="tabpanel"
                    aria-labelledby="pills-profile-tab"
                  >
                    {(loading1 || loadingPendingRequests) && (
                      <div className="text-center my-4">
                        <img
                          src={SkorDefaultLoader}
                          className="defaultSkorLoader"
                          alt="Example"
                        />
                      </div>
                    )}
                    <div className="edit-profile">
                      <div
                        className="tab-pane fade show active"
                        id="pills-home"
                        role="tabpanel"
                        aria-labelledby="pills-home-tab"
                      >
                        <div className="edit-profile">
                          <div
                            className="Notifications-row new-notifications-row"
                            style={{ marginTop: "12px" }}
                          >
                            {/* ── Landlord pending requests ── */}
                            {!loadingPendingRequests &&
                              (landlordRequests ?? []).map((request) => {
                                const landlord = request.landlord_id ?? {};
                                const landlordName =
                                  `${landlord.fname_companyName ?? ""} ${landlord.lname ?? ""}`.trim() ||
                                  landlord.email ||
                                  "";
                                const shareOption =
                                  requestShareSelectors[request._id] ?? null;
                                const dropdownOpen =
                                  requestDropdownOpen[request._id] ?? false;
                                const isProcessing =
                                  processingRequestId === request._id;
                                const hasSelection = shareOption !== null;

                                return (
                                  <div
                                    className="Notifications-wapper tt landlord-req-card"
                                    style={{
                                      opacity: 1,
                                      marginBottom: "12px",
                                      padding: "16px 8px",
                                    }}
                                    key={request._id}
                                  >
                                    <div className="mobile-profile-info-box">
                                      <div className="userImageWrapper landlord-req-avatar-wrapper">
                                        <div className="landlord-req-avatar">
                                          {getInitials(landlordName)}
                                        </div>
                                      </div>
                                      <div className="userDetails user-name">
                                        <div className="d-flex align-items-center gap-1 mb-1 flex-wrap">
                                          <h4
                                            style={{
                                              marginBottom: 0,
                                            }}
                                          >
                                            {landlordName}
                                          </h4>
                                          <span
                                            className="pending-badge"
                                            style={{
                                              background: "#f0a027",
                                              color: "#7D4C14",
                                              padding: "2px 8px",
                                              borderRadius: "100px",
                                              fontSize: "11px",
                                              fontWeight: 600,
                                              lineHeight: "normal",
                                            }}
                                          >
                                            {_t("pending", "Pending")}
                                          </span>
                                        </div>
                                        <div className="landlord-req-selector position-relative">
                                          <button
                                            type="button"
                                            className={`landlord-req-selector-btn${hasSelection ? " selected" : ""}`}
                                            onClick={() =>
                                              toggleRequestDropdown(request._id)
                                            }
                                          >
                                            {hasSelection && (
                                              <img
                                                src={
                                                  shareOption.type ===
                                                  "application"
                                                    ? "/images/multiple_users_icon_unfilled.svg"
                                                    : UserIcon
                                                }
                                                alt=""
                                                style={{ width: "12px" }}
                                              />
                                            )}
                                            <span className="landlord-req-selector-text">
                                              {!hasSelection
                                                ? _t(
                                                    "selectWhatToShare",
                                                    "Select what you want to share"
                                                  )
                                                : shareOption.type ===
                                                    "myProfile"
                                                  ? _t(
                                                      "myProfileOnly",
                                                      "My profile only"
                                                    )
                                                  : getApplicationTitle(
                                                      shareOption.app,
                                                      _t
                                                    )}
                                            </span>
                                            <svg
                                              className={`landlord-req-chevron${dropdownOpen ? " open" : ""}`}
                                              width="12"
                                              height="12"
                                              viewBox="0 0 16 16"
                                              fill="none"
                                            >
                                              <path
                                                d="M4 6L8 10L12 6"
                                                stroke={
                                                  hasSelection
                                                    ? "#253837"
                                                    : "#7D4C14"
                                                }
                                                strokeWidth="1.5"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                              />
                                            </svg>
                                          </button>
                                          {dropdownOpen && (
                                            <div className="landlord-req-dropdown">
                                              <div
                                                className="landlord-req-option"
                                                onClick={() =>
                                                  selectRequestShare(
                                                    request._id,
                                                    { type: "myProfile" }
                                                  )
                                                }
                                              >
                                                <img
                                                  src={UserIcon}
                                                  alt=""
                                                  style={{ width: "14px" }}
                                                />
                                                <span>
                                                  {_t(
                                                    "myProfileOnly",
                                                    "My profile only"
                                                  )}
                                                </span>
                                              </div>
                                              {(myApplications ?? []).map(
                                                (app) => (
                                                  <div
                                                    key={app._id}
                                                    className="landlord-req-option"
                                                    onClick={() =>
                                                      selectRequestShare(
                                                        request._id,
                                                        {
                                                          type: "application",
                                                          app,
                                                        }
                                                      )
                                                    }
                                                  >
                                                    <img
                                                      src="/images/multiple_users_icon_unfilled.svg"
                                                      alt=""
                                                      style={{
                                                        width: "14px",
                                                      }}
                                                    />
                                                    <span>
                                                      {getApplicationTitle(
                                                        app,
                                                        _t
                                                      )}
                                                    </span>
                                                  </div>
                                                )
                                              )}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                    <div
                                      className="share-it"
                                      style={{
                                        gap: "8px",
                                        display: "flex",
                                      }}
                                    >
                                      <button
                                        type="button"
                                        className={`landlord-req-accept-btn${hasSelection ? " active" : ""}`}
                                        onClick={() =>
                                          handleAcceptLandlordRequest(request)
                                        }
                                        disabled={isProcessing || !hasSelection}
                                        aria-label="accept"
                                      >
                                        <CheckIcon fontSize="small" />
                                      </button>
                                      <button
                                        type="button"
                                        className="landlord-req-dismiss-btn"
                                        onClick={() =>
                                          handleRejectLandlordRequest(
                                            request._id
                                          )
                                        }
                                        disabled={isProcessing}
                                        aria-label="dismiss"
                                      >
                                        <CloseRoundedIcon fontSize="small" />
                                      </button>
                                    </div>
                                  </div>
                                );
                              })}
                            {!loadingPendingRequests &&
                              Array.isArray(filteredShortlisted) &&
                              !haveValue(searchText) &&
                              filteredShortlisted.length > 0 &&
                              filteredShortlisted?.map((landlord) => {
                                let property_linked = isPropertyLinked(
                                  landlord?.landlordid
                                );
                                return (
                                  <>
                                    {
                                      <div
                                        className="Notifications-wapper tt d-flex align-items-center justify-content-between"
                                        key={landlord?.landlordid}
                                        style={{
                                          padding: "16px 8px",
                                          border: "1px solid #E5E7EB",
                                          marginBottom: "12px",
                                          opacity: 1,
                                          borderRadius: "8px",
                                        }}
                                      >
                                        <div
                                          className="mobile-profile-info-box d-flex align-items-center mobile-profile-gap"
                                          style={{ gap: "15px" }}
                                        >
                                          <div
                                            className="userImageWrapper"
                                            style={{
                                              margin: 0,
                                              display: "flex",
                                              alignItems: "center",
                                            }}
                                          >
                                            <InitialsAvatar
                                              name={
                                                `${landlord?.landlord_fname ?? ""} ${landlord?.landlord_lname ?? ""}`.trim() ||
                                                emailPrefix(
                                                  landlord?.landlord_email ?? ""
                                                )
                                              }
                                            />
                                          </div>
                                          <div
                                            className="userDetails d-flex flex-column"
                                            style={{
                                              textAlign: "left",
                                            }}
                                          >
                                            <h4
                                              style={{
                                                margin: 0,
                                                fontSize: "13px",
                                                fontWeight: 600,
                                                color: "#1E2528",
                                                lineHeight: 1,
                                              }}
                                            >
                                              {landlord?.landlord_fname}{" "}
                                              {landlord?.landlord_lname}
                                            </h4>
                                            <p className="d-flex align-items-center gap-2 invitation-sent-text mb-1 grey-tag">
                                              {landlord?.application_id ? (
                                                <img
                                                  src="/images/multiple_users_icon_unfilled.svg"
                                                  alt="User Icon"
                                                />
                                              ) : (
                                                <img
                                                  src={UserIcon}
                                                  alt="user"
                                                />
                                              )}
                                              <p>
                                                {landlord?.application_id
                                                  ? (getApplicationBadge(
                                                      landlord.application_id
                                                    ) ??
                                                    _t(
                                                      "myProfileOnly",
                                                      "My profile only"
                                                    ))
                                                  : _t(
                                                      "myProfileOnly",
                                                      "My profile only"
                                                    )}
                                              </p>
                                            </p>
                                            {/* {haveValue(property_linked) &&
                                              (property_linked?.propertyid
                                                ?.length > 0 ? (
                                                <p className="d-flex gap-2-vw locationText">
                                                  {property_linked?.propertyid
                                                    ?.map(
                                                      (props) =>
                                                        `${props?.street} - ${
                                                          Number.isNaN(
                                                            parseInt(
                                                              props?.street_number,
                                                              10
                                                            )
                                                          )
                                                            ? "N/A"
                                                            : String(
                                                                parseInt(
                                                                  props?.street_number,
                                                                  10
                                                                )
                                                              )
                                                        }, ${
                                                          props.floor_number
                                                        }-${props.flat_number}`
                                                    )
                                                    .join(" / ")}
                                                </p>
                                              ) : (
                                                <p className="d-flex gap-2-vw d-flex align-items-center gap-1 invitation-sent-text mb-1">
                                                  {_t("noPropertyLinked")}
                                                </p>
                                              ))} */}
                                          </div>
                                        </div>

                                        <div
                                          className="share-it"
                                          style={{
                                            display: "flex",
                                            alignItems: "center",
                                            margin: 0,
                                          }}
                                        >
                                          <Link
                                            onClick={(e) => {
                                              e.preventDefault();
                                              onClose();
                                              setShowRemove(true);
                                              setRemoveLandlordId(
                                                landlord?.landlordid
                                              );
                                            }}
                                            className="share-btn"
                                            style={{
                                              border: "none",
                                              borderRadius: "100px",
                                              padding: "8px 16px",
                                              color: "#1F2937",
                                              fontWeight: "600",
                                              fontSize: "13px",
                                              background: "#ECFDF5",
                                              textDecoration: "none",
                                              display: "flex",
                                              alignItems: "center",
                                              justifyContent: "center",
                                              lineHeight: "normal",
                                              minHeight: "32px",
                                            }}
                                          >
                                            {_t("stopSharing", "stopSharing")}
                                          </Link>
                                        </div>
                                      </div>
                                    }
                                  </>
                                );
                              })}
                            {pendingShareRequests?.length > 0 &&
                              pendingShareRequests.map((request) => {
                                const requestId = request?._id;
                                const requestEmail =
                                  getPendingRequestEmail(request);
                                const requestDisplayName =
                                  getPendingRequestDisplayName(request);
                                const createdAt = request?.created_at;
                                const isCanceling =
                                  cancelingRequestId === requestId;

                                return (
                                  <div
                                    className="Notifications-wapper tt d-flex align-items-center justify-content-between"
                                    key={requestId || requestEmail}
                                    style={{
                                      padding: "16px 8px",
                                      border: "1px solid #E5E7EB",
                                      marginBottom: "12px",
                                      opacity: 1,
                                      borderRadius: "8px",
                                    }}
                                  >
                                    <div
                                      className="mobile-profile-info-box d-flex align-items-center mobile-profile-gap"
                                      style={{ gap: "15px" }}
                                    >
                                      <div
                                        className="userImageWrapper"
                                        style={{
                                          margin: 0,
                                          display: "flex",
                                          alignItems: "center",
                                        }}
                                      >
                                        <InitialsAvatar
                                          name={requestDisplayName}
                                        />
                                      </div>
                                      <div
                                        className="userDetails d-flex flex-column"
                                        style={{
                                          gap: "6px",
                                          textAlign: "left",
                                        }}
                                      >
                                        <h4
                                          style={{
                                            margin: 0,
                                            fontSize: "15px",
                                            fontWeight: 600,
                                            color: "#1E2528",
                                            lineHeight: 1,
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "7px",
                                          }}
                                        >
                                          <span
                                            className="email"
                                            style={{
                                              textTransform: "none",
                                              maxWidth: "216px",
                                              whiteSpace: "nowrap",
                                              overflow: "hidden",
                                              textOverflow: "ellipsis",
                                            }}
                                          >
                                            {requestDisplayName}
                                          </span>
                                          <span
                                            className="pending-badge"
                                            style={{
                                              background: "#f0a027",
                                              color: "#7D4C14",
                                              padding: "2px 8px",
                                              borderRadius: "100px",
                                              fontSize: "11px",
                                              fontWeight: 600,
                                            }}
                                          >
                                            {_t("pending", "Pending")}
                                          </span>
                                        </h4>
                                        <div
                                          className="d-flex align-items-center"
                                          style={{
                                            background: "#F3F4F6",
                                            borderRadius: "100px",
                                            padding: "4px 12px",
                                            width: "fit-content",
                                            gap: "6px",
                                          }}
                                        >
                                          {request?.application_id ? (
                                            <img
                                              src="/images/multiple_users_icon_unfilled.svg"
                                              alt="User Icon"
                                              className="share-profile-pill-icon"
                                              style={{
                                                width: "14px",
                                                height: "14px",
                                                opacity: 0.6,
                                                margin: 0,
                                                display: "block",
                                                objectFit: "contain",
                                                flexShrink: 0,
                                                position: "static",
                                                transform: "none",
                                                top: "auto",
                                              }}
                                            />
                                          ) : (
                                            <img
                                              src={UserIcon}
                                              alt="user"
                                              className="share-profile-pill-icon"
                                              style={{
                                                width: "14px",
                                                height: "14px",
                                                opacity: 0.6,
                                                margin: 0,
                                                display: "block",
                                                objectFit: "contain",
                                                flexShrink: 0,
                                                position: "static",
                                                transform: "none",
                                                top: "auto",
                                              }}
                                            />
                                          )}

                                          <span
                                            style={{
                                              fontSize: "12px",
                                              color: "#4B5563",
                                              fontWeight: 500,
                                              lineHeight: 1,
                                            }}
                                          >
                                            {request?.application_id
                                              ? (getApplicationBadge(
                                                  request.application_id
                                                ) ??
                                                _t(
                                                  "myProfileOnly",
                                                  "My profile only"
                                                ))
                                              : _t(
                                                  "myProfileOnly",
                                                  "My profile only"
                                                )}
                                          </span>
                                        </div>
                                        {/* <p className="invitation-sent-text">
                                          {createdAt
                                            ? `${_t("invitationSent")} ${moment(
                                                createdAt
                                              ).fromNow()}`
                                            : `${_t("invitationSent")}`}
                                        </p> */}
                                      </div>
                                    </div>

                                    <div
                                      className="share-it"
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        margin: 0,
                                      }}
                                    >
                                      <button
                                        type="button"
                                        onClick={() =>
                                          handleCancelShareRequest(requestId)
                                        }
                                        disabled={isCanceling}
                                        className="share-btn"
                                        style={{
                                          border: "none",
                                          borderRadius: "100px",
                                          padding: "8px 16px",
                                          color: "#1F2937",
                                          fontWeight: "600",
                                          fontSize: "13px",
                                          background: "#ECFDF5",
                                          textDecoration: "none",
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          lineHeight: "normal",
                                          minHeight: "32px",
                                        }}
                                      >
                                        {isCanceling
                                          ? `${_t("canceling", "Canceling")}...`
                                          : _t("cancel", "Cancel")}
                                      </button>
                                    </div>
                                  </div>
                                );
                              })}
                            {!loading1 &&
                              !loadingPendingRequests &&
                              landlordRequests?.length === 0 &&
                              pendingShareRequests?.length === 0 &&
                              filteredShortlisted.length === 0 && (
                                <div className="spm-empty-state">
                                  {_t(
                                    "youHaventSharedYourProfileWithAnyLandlordOrAgentYet"
                                  )}
                                </div>
                              )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {showConsent && (
            <ConsentStepModal
              user={user}
              isPopup={true}
              onClose={() => setShowConsent(false)}
              loadSharedProfiles={loadSharedProfiles}
              loadUserTenant={loadUserTenant}
              selectedLandlord={selectedLandlord}
              shareProfileWithLandlord={() =>
                sendShareRequest(selectedLandlord?.email)
              }
            />
          )}
        </div>
      </div>
    )
  );
};

export default ShareProfileModal;
